// src/crm/ugc/RateField.tsx
//
// « Rémunération pour 1 000 vues » d'un membre (migration 0021), dans sa fiche et dans l'onglet
// « Montants à fixer » : saisie en euros (« 2,50 »), envoyée en centimes, puis « Enregistrer ».
// Le premier montant prévient la personne par e-mail (« Ton montant est fixé ») ; pour un
// changement, la case « Prévenir la personne » l'envoie aussi. Les montants déjà figés gardent leur
// tarif. Sous le champ : la date de la dernière modification, ou ce que la personne voit tant qu'il
// manque (« En cours d'analyse du montant attribuable »).

import { useEffect, useId, useState } from 'react'
import { formatRateInput, highRateQuestion, parseRateInput } from '../../../functions/lib/ugc/memberRate'
import { formatEuros } from '../../../functions/lib/ugc/rules'
import type { UgcMember } from '../../lib/ugcTypes'
import { formatDate } from './format'
import { HighRateConfirm } from './HighRateConfirm'
import { useSetMemberRate } from './hooks'

export type RateSaved = { emailed: boolean; frozen: number }

/** Après l'enregistrement : e-mail parti ou pas, vidéos figées au passage (phrases à la suite). */
export function savedDetails(result: RateSaved, first: boolean): string[] {
  const parts: string[] = []
  if (result.emailed) parts.push(first ? 'La personne est prévenue par e-mail.' : 'La personne est prévenue du changement.')
  else if (first) parts.push("L'e-mail à la personne n'est pas parti : préviens-la par le chat.")
  if (result.frozen > 0) {
    parts.push(
      result.frozen > 1
        ? `${result.frozen} vidéos qui l'attendaient ont leur montant.`
        : "1 vidéo qui l'attendait a son montant.",
    )
  }
  return parts
}

/**
 * `onSaved` : appelé après l'enregistrement à la place du message sous le champ (l'onglet « Montants
 * à fixer » l'affiche au-dessus de sa liste, d'où la carte disparaît).
 */
export function RateField({
  member,
  compact = false,
  onSaved,
}: {
  member: UgcMember
  compact?: boolean
  onSaved?: (saved: { result: RateSaved; cents: number; first: boolean }) => void
}) {
  const setRate = useSetMemberRate()
  const id = useId()
  const notifyId = useId()
  const current = member.rateCentsPerThousand
  const [text, setText] = useState(current === null ? '' : formatRateInput(current))
  const [notify, setNotify] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  // Montant élevé en attente de confirmation (plus de 10 € pour 1 000 vues).
  const [confirming, setConfirming] = useState(false)
  // Le champ suit le montant enregistré (autre onglet, relecture après l'enregistrement).
  useEffect(() => {
    setText(current === null ? '' : formatRateInput(current))
  }, [current])

  const cents = parseRateInput(text)
  const invalid = text.trim() !== '' && cents === null
  const first = current === null
  const unchanged = cents !== null && cents === current

  function save(confirmed = false) {
    if (cents === null) return
    setSaved(null)
    if (!confirmed && highRateQuestion(cents) !== null) {
      setConfirming(true)
      return
    }
    setConfirming(false)
    setRate.mutate(
      { id: member.id, rateCentsPerThousand: cents, expectedRateCentsPerThousand: current, notify: !first && notify },
      {
        onSuccess: (result) => {
          setNotify(false)
          if (onSaved) onSaved({ result, cents, first })
          else setSaved(['Montant enregistré.', ...savedDetails(result, first)].join(' '))
        },
      },
    )
  }

  const name = member.displayName || 'cette personne'
  return (
    <div className="ugc-rate">
      <div className="ugc-field">
        <label htmlFor={id}>{compact ? `Montant pour 1 000 vues de ${name}` : 'Montant en euros'}</label>
        <div className="ugc-rate-input">
          <input
            id={id}
            className="ugc-input"
            inputMode="decimal"
            autoComplete="off"
            placeholder="2,50"
            value={text}
            aria-invalid={invalid}
            aria-describedby={`${id}-help`}
            onChange={(e) => {
              setText(e.target.value)
              setSaved(null)
              setConfirming(false)
            }}
          />
          <span aria-hidden="true">€</span>
        </div>
      </div>
      {!first && (
        <label className="ugc-check" htmlFor={notifyId}>
          <input id={notifyId} type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          <span>Prévenir la personne par e-mail</span>
        </label>
      )}
      <div className="ugc-actions">
        <button
          type="button"
          className={first ? 'ugc-btn ugc-btn--go' : 'ugc-btn'}
          disabled={setRate.isPending || cents === null || unchanged}
          onClick={() => save()}
        >
          {setRate.isPending ? 'Enregistrement…' : first ? 'Fixer le montant' : 'Enregistrer le montant'}
        </button>
      </div>
      {confirming && cents !== null && (
        <HighRateConfirm
          question={highRateQuestion(cents) ?? ''}
          busy={setRate.isPending}
          onConfirm={() => save(true)}
          onCancel={() => setConfirming(false)}
        />
      )}
      <p id={`${id}-help`} className="ugc-muted">
        {invalid
          ? 'Écris un montant en euros, de 0,01 à 100 (par exemple 2,50).'
          : current === null
            ? "Pas encore fixé : la personne voit « En cours d'analyse du montant attribuable » et ses relevés attendent. Le premier montant la prévient par e-mail."
            : `${formatEuros(current)} pour 1 000 vues${member.rateSetAt ? `, modifié le ${formatDate(member.rateSetAt)}` : ''}. Les montants déjà figés gardent leur tarif.`}
      </p>
      {saved && (
        <p role="status" className="ugc-notice">
          {saved}
        </p>
      )}
      {setRate.error && (
        <p role="alert" className="ugc-error">
          {setRate.error.message}
        </p>
      )}
    </div>
  )
}
